// src/features/admin/dashboardApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export type RangeType =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';

interface SalesOrders {
  sales: number;
  orders: number;
}

interface DailySales {
  day: string;
  user: SalesOrders;
  guest: SalesOrders;
  total: SalesOrders;
}

interface PeriodSales {
  user: SalesOrders;
  guest: SalesOrders;
  total: SalesOrders;
}

interface TopProduct {
  product_id: string;
  user_quantity: number;
  guest_quantity: number;
  total_quantity: number;
  user_value: number;
  guest_value: number;
  total_value: number;
}

interface NewUsersBreakdown {
  current_week: number;
  last_week: number;
}

interface NewUsers {
  user: NewUsersBreakdown;
  guest: NewUsersBreakdown;
  total: NewUsersBreakdown;
}

interface GrowthItem {
  current: number;
  last: number;
}

interface Growth {
  user: GrowthItem;
  guest: GrowthItem;
  total: GrowthItem;
}

interface RangeSummary {
  start: string;
  end: string;
  user: SalesOrders;
  guest: SalesOrders;
  total: SalesOrders;
}

interface RangeMetric {
  current: number;
  previous: number;
  diff: number;
  percent: number;
}

interface RangeMetricGroup {
  sales: RangeMetric;
  orders: RangeMetric;
}

interface RangeComparison {
  user: RangeMetricGroup;
  guest: RangeMetricGroup;
  total: RangeMetricGroup;
}

interface RangeInfo {
  requested_range_type: RangeType;
  range_summary: RangeSummary;
  previous_range: RangeSummary;
  comparison: RangeComparison;
}

export interface DashboardAnalyticsResponse {
  success: boolean;
  daily_sales: DailySales;
  weekly_sales: Record<string, PeriodSales>;
  monthly_sales: Record<string, PeriodSales>;
  top_products: TopProduct[];
  new_users: NewUsers;
  month_growth: Growth;
  quarter_growth: Growth;
  range: RangeInfo;
}

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set(
        'authkey',
        '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3'
      );
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  endpoints: (build) => ({
    getDashboardAnalytics: build.query<DashboardAnalyticsResponse, RangeType>({
      query: (rangeType) => ({
        url: 'admin/dashboard-analytics',
        method: 'POST',
        body: { range_type: rangeType },
      }),
    }),
  }),
});

export const { useGetDashboardAnalyticsQuery } = dashboardApi;